<template>
  <v-dialog
    v-model="open"
    persistent
    :max-width="getWidth"
    :fullscreen="isMobileBreakpoint"
  >
    <v-card :style="step === 1 ? 'max-height: 521px' : 'auto'">
      <v-toolbar
        flat
        class="px-2"
        elevation="0"
        :class="[isMobileBreakpoint ? 'px-3' : 'px-2']"
      >
        <v-toolbar-title
          v-if="step === 2"
          class="body-regular-medium font-weight-medium"
          :class="{ 'neutral-20-dark--text': !$vuetify.theme.dark }"
          style="line-height: 120%; white-space: normal"
        >
          {{ $t('message.pages.drm.cademiIntegration.step1.modalTitle') }}
          <p
            class="text-caption mb-0"
            style="white-space: normal"
            :class="{
              'neutral-40-light--text': !$vuetify.theme.dark,
            }"
          >
            {{ $t('message.pages.drm.cademiIntegration.step1.modalSubtitle') }}
            <a
              size="xsmall"
              class="neutral-30-base--text body-bold-small"
              @click="openLink"
            >
              {{
                $t(
                  'message.pages.drm.cademiIntegration.step1.modalLinkDescription'
                )
              }}
              <v-icon size="12px"> mdi-open-in-new </v-icon>
            </a>
          </p>
        </v-toolbar-title>
        <v-spacer />
        <v-icon
          @click="close"
          class="close-icon"
          size="24"
          color="grey"
          style="cursor: pointer"
        >
          mdi-close
        </v-icon>
      </v-toolbar>
      <v-card-text
        ref="cardText"
        class="text-center"
        :class="[
          step === 1 ? 'mt-12' : 'mt-0 pt-0',
          isMobileBreakpoint && step === 2 ? 'pa-5' : 'pa-7',
        ]"
        :style="
          isMobileBreakpoint
            ? { height: 'calc(100vh - 80px)', overflowY: 'auto' }
            : {}
        "
      >
        <template v-if="step === 1">
          <div
            class="d-flex align-center justify-center"
            style="gap: 12px; height: 56px"
          >
            <img
              class="svg-icon"
              :src="svgIcons.cademi"
              width="56px"
              alt="Cademi"
            />
            <img
              class="svg-icon"
              :src="svgIcons.chevRight"
              width="24px"
              alt="Arrow"
            />
            <img
              class="svg-icon"
              :src="svgIcons.bfPanda"
              width="48px"
              alt="Panda"
            />
          </div>
          <div
            class="body-bold-medium neutral-20-dark--text mt-4 mb-6"
            :style="{
              'white-space': isMobileBreakpoint ? 'pre-line' : 'normal',
            }"
          >
            {{ $t('message.pages.drm.cademiIntegration.step1.title') }}
          </div>
          <div
            class="body-regular-small neutral-40-light--text mb-6"
            :class="isMobileBreakpoint ? 'px-5' : 'px-14'"
          >
            <p
              class="mb-7"
              :style="{
                'white-space': isMobileBreakpoint ? 'normal' : 'pre-line',
              }"
            >
              {{ $t('message.pages.drm.cademiIntegration.step1.description') }}
            </p>
            <p>
              <a
                :href="$t('message.pages.drm.cademiIntegration.step1.link')"
                target="_blank"
                rel="noopener"
                class="brand-60-base--text body-bold-small"
                style="text-decoration: none"
              >
                {{
                  $t(
                    'message.pages.drm.cademiIntegration.step1.linkDescription'
                  )
                }}
                <v-icon size="14px" class="brand-60-base--text mb-1">
                  mdi-open-in-new
                </v-icon>
              </a>
            </p>
          </div>
          <base-btn @click="nextStep" class="body-regular-small mb-16">
            {{ $t('message.pages.drm.cademiIntegration.step1.action') }}
          </base-btn>
        </template>
        <template v-else>
          <v-expansion-panels
            :value="activePanel"
            flat
            accordion
            mandatory
            readonly
          >
            <step-panel
              :step-number="1"
              @nextStep="nextPanel"
              @headerClick="showPanel"
              :completed="isPanelCompleted(1)"
              :nextLoading="isFetching"
              :nextDisabled="!groupName.trim()"
            >
              <template #title>
                {{ $t('message.pages.drm.enterDrmGroupName') }}
              </template>
              <template #content>
                <div>
                  <v-input
                    v-model="groupName"
                    :style="{
                      width: '100%',
                      marginBottom: '14px',
                      maxWidth: isMobileBreakpoint ? undefined : '332px',
                    }"
                    :error="dirty && hasGroupNameError"
                    :error-messages="dirty ? groupNameErrors : []"
                    :hide-details="!hasGroupNameError"
                    size="small"
                    @input="dirty = true"
                    @blur="dirty = true"
                    autocomplete="off"
                    :counter="groupName.length > 255 ? 255 : undefined"
                    dense
                    solo
                    flat
                  />
                </div>
              </template>
            </step-panel>

            <step-panel
              @nextStep="nextPanel"
              @headerClick="showPanel"
              :step-number="2"
              :completed="isPanelCompleted(2)"
            >
              <template #title>
                <div>
                  {{ $t('message.pages.drm.cademiIntegration.panel2.title') }}
                  <a
                    :href="
                      $t('message.pages.drm.cademiIntegration.panel2.link')
                    "
                    target="_blank"
                    rel="noopener"
                    class="brand-60-base--text body-bold-small"
                    style="text-decoration: none"
                    @click.stop
                  >
                    {{
                      $t(
                        'message.pages.drm.cademiIntegration.panel2.linkDescription'
                      )
                    }}
                    <v-icon size="14px" class="brand-60-base--text mb-1">
                      mdi-open-in-new
                    </v-icon>
                  </a>
                </div>
              </template>
              <template #content>
                <div class="mb-5">
                  <BaseLazyImage
                    :src="
                      require(`@/assets/integration/cademi/Cademi-1-${currentLanguage}.png`)
                    "
                    alt="Cademi Integration - Step 2"
                    height="365px"
                  />
                </div>
              </template>
            </step-panel>

            <step-panel
              @nextStep="nextPanel"
              @headerClick="showPanel"
              :step-number="3"
              :completed="isPanelCompleted(3)"
            >
              <template #title>
                <div>
                  <span
                    v-html="
                      $t('message.pages.drm.cademiIntegration.panel3.title')
                    "
                  ></span>
                </div>
              </template>
              <template #content>
                <div class="mb-5">
                  <BaseLazyImage
                    :src="
                      require(`@/assets/integration/cademi/Cademi-2-${currentLanguage}.png`)
                    "
                    alt="Cademi Integration - Step 3"
                    height="239px"
                  />
                </div>
              </template>
            </step-panel>

            <step-panel
              @nextStep="nextPanel"
              @headerClick="showPanel"
              :step-number="4"
              :completed="isPanelCompleted(4)"
            >
              <template #title>
                <div>
                  <span
                    v-html="
                      $t('message.pages.drm.cademiIntegration.panel4.title')
                    "
                  ></span>
                </div>
              </template>
              <template #content>
                <div class="mb-5">
                  <BaseLazyImage
                    :src="
                      require(`@/assets/integration/cademi/Cademi-3-${currentLanguage}.png`)
                    "
                    alt="Cademi Integration - Step 4"
                    height="239px"
                  />
                </div>
              </template>
            </step-panel>

            <step-panel
              @nextStep="nextPanel"
              @headerClick="showPanel"
              :step-number="5"
              :completed="isPanelCompleted(5)"
            >
              <template #title>
                <span>
                  <span
                    v-html="
                      $t('message.pages.drm.cademiIntegration.panel5.title')
                    "
                  ></span>
                </span>
              </template>
              <template #content>
                <div class="mb-5 mt-4">
                  <div
                    class="d-flex align-center justify-space-between mb-5 panel-5-content"
                    style="gap: 16px"
                  >
                    <base-textarea
                      v-model="drmGroup.id"
                      :label="
                        $t(
                          'message.pages.drm.cademiIntegration.panel5.groupIdLabel'
                        )
                      "
                      auto-grow
                      rows="1"
                      hide-details
                      flat
                      dense
                      readonly
                      class="input-cademi"
                    >
                      <template #append>
                        <v-icon
                          style="font-size: 16px; cursor: pointer"
                          class="neutral-60--text"
                          @click.stop="copyToClipboard(drmGroup.id)"
                        >
                          mdi-content-copy
                        </v-icon>
                      </template>
                    </base-textarea>
                    <base-btn
                      type="tertiary"
                      size="small"
                      @click="copy(drmGroup.id, 'copiedGroupId')"
                      :disabled="!drmGroup.id"
                    >
                      <v-icon class="copy-icon" size="20">panda-copy</v-icon>
                      {{
                        $t(
                          'message.pages.drm.cademiIntegration.panel5.copyButton'
                        )
                      }}
                    </base-btn>
                  </div>
                </div>
                <div class="mb-5 mt-4">
                  <div
                    class="d-flex align-center justify-space-between mb-5 panel-5-content"
                    style="gap: 16px"
                  >
                    <v-input
                      :label="
                        $t(
                          'message.pages.drm.cademiIntegration.panel5.secretKeyLabel'
                        )
                      "
                      :type="showPass ? 'text' : 'password'"
                      v-model="drmGroup.secret"
                      class="flex-grow-1"
                      size="small"
                      hide-details
                      dense
                      flat
                      readonly
                    >
                      <template #append>
                        <v-icon
                          small
                          style="font-size: 16px; cursor: pointer"
                          @click.stop="showPass = !showPass"
                          class="neutral-60--text"
                        >
                          {{
                            showPass
                              ? 'panda-eye-appearing'
                              : 'panda-eye-hidden'
                          }}
                        </v-icon>
                      </template>
                    </v-input>
                    <base-btn
                      type="tertiary"
                      size="small"
                      @click="copy(drmGroup.secret, 'copiedSecretKey')"
                      :disabled="!drmGroup.secret"
                    >
                      <v-icon class="copy-icon" size="20">panda-copy</v-icon>
                      {{
                        $t(
                          'message.pages.drm.cademiIntegration.panel5.copyButton'
                        )
                      }}
                    </base-btn>
                  </div>
                </div>
                <div class="mb-5">
                  <BaseLazyImage
                    :src="
                      require(`@/assets/integration/cademi/Cademi-4-${currentLanguage}.png`)
                    "
                    alt="Cademi Integration - Step 5"
                    height="239px"
                  />
                </div>
              </template>
            </step-panel>

            <step-panel
              :step-number="6"
              @nextStep="nextPanel"
              @headerClick="showPanel"
              @openIntegration="openIntegration"
              @backToList="close"
              :completed="isPanelCompleted(5)"
              isFinalStep
            >
              <template #title>
                <span>
                  {{ $t('message.pages.drm.cademiIntegration.panel6.title') }}
                </span>
              </template>
            </step-panel>
          </v-expansion-panels>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import StepPanel from '@/components/StepPanel.vue'
import drmApi from '@/services/drmApi'
import BaseTextarea from '@/components/BaseComponents/BaseTextarea.vue'
import BaseLazyImage from '@/components/BaseLazyImage.vue'

export default {
  name: 'DrmCademiIntegration',
  components: {
    StepPanel,
    BaseTextarea,
    BaseLazyImage,
  },
  props: {
    selectedGroup: {
      type: Object,
      default: () => ({}),
    },
    isEdit: {
      type: Boolean,
    },
  },
  data() {
    return {
      step: 1,
      open: true,
      activePanel: 0,
      previousPanel: 0,
      isFetching: false,
      groupName: 'DRM Panda Video - Cademi',
      drmGroup: {
        id: null,
        secret: null,
      },
      completedPanels: [],
      panelIndexMap: {
        CREATE_GROUP: 0,
        COPY: 4,
      },
      showPass: false,
      dirty: false,
      isCopyCheckpoint: false,
    }
  },
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    getWidth() {
      return this.step === 1 ? '762px' : '822px'
    },
    currentLanguage() {
      const lang = this.$i18next.language || this.$i18n?.locale || 'en'
      return lang.split('-')[0]
    },
    isPanelCompleted() {
      return (step) => {
        return this.completedPanels.includes(step)
      }
    },
    groupNameErrors() {
      const errors = []
      if (this.groupName.length === 0) {
        errors.push(this.$t('message.requiredField'))
      }
      if (this.groupName.length > 255) {
        errors.push(this.$t('message.maxLengthError', { maxLength: 255 }))
      }
      return errors
    },
    hasGroupNameError() {
      return this.groupNameErrors.length > 0
    },
    isDarkTheme() {
      return this.$vuetify.theme.dark
    },
    svgIcons() {
      const chevRightName = this.isDarkTheme
        ? 'chev-right-dark'
        : 'chev-right-light'
      return {
        cademi: require('@/assets/pandaIcons/plataformas-cademi.svg'),
        chevRight: require(`@/assets/pandaIcons/${chevRightName}.svg`),
        bfPanda: require('@/assets/bf_panda.svg'),
      }
    },
  },
  mounted() {
    this.open = true
    console.log(this.selectedGroup)
    if (Object.keys(this.selectedGroup)?.length) {
      this.drmGroup = this.selectedGroup
      this.groupName = this.selectedGroup.name
      this.step = 2
    }
  },
  beforeDestroy() {
    this.showCrispButton()
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    copy(value, key) {
      const text = this.$t(`message.pages.drm.cademiIntegration.panel5.${key}`)
      navigator.clipboard
        .writeText(value)
        .then(() => {
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            type: 'success',
            text,
          })
        })
        .catch((err) => {
          console.error('Error copying:', err)
        })
    },
    openIntegration() {
      this.$root.$emit('navigateToGroup', this.drmGroup)
      this.close()
    },
    openLink() {
      window.open(
        this.$t('message.pages.drm.cademiIntegration.step1.modalLink'),
        '_blank'
      )
    },
    async goToPanel(newVal) {
      if (newVal === this.panelIndexMap.COPY) {
        this.isCopyCheckpoint = true
      }

      if (this.isFetching) return

      const oldVal = this.previousPanel
      const isLeavingDrmGroup = oldVal === this.panelIndexMap.CREATE_GROUP
      const groupNameChanged = this.groupName !== this.drmGroup?.name

      if (
        isLeavingDrmGroup &&
        (!this.groupName || this.groupName.trim() === '')
      ) {
        return
      }

      if (groupNameChanged && !this.hasGroupNameError) {
        await this.createOrUpdateDrmGroup()
      }

      if (this.isCopyCheckpoint && !this.drmGroup.active) {
        this.activateDrmGroup()
      }

      this.previousPanel = newVal
      this.activePanel = newVal

      this.checkStepCompletion(newVal, oldVal)
    },
    async nextPanel() {
      const next = this.activePanel + 1
      await this.goToPanel(next)
    },
    async showPanel(panel) {
      if (this.activePanel === panel && panel !== 0) {
        await this.goToPanel(panel - 1)
      } else if (this.activePanel !== panel) {
        await this.goToPanel(panel)
      }
    },
    nextStep() {
      this.step = 2
    },
    close() {
      if (this.drmGroup?.id) {
        this.$root.$emit('reloadDrm')
        this.$root.$emit('addNewDrmGroup', this.drmGroup)
        if (this.drmGroup.active && !this.isEdit) {
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            text: this.$t('message.pages.drm.newGroupMessage'),
            type: 'success',
          })
        } else if (this.drmGroup.active && this.isEdit) {
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            text: this.$t('message.pages.drm.editGroupMessage'),
            type: 'success',
          })
        } else {
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            text: this.$t('message.pages.drm.incompleteIntegrationMessage'),
            type: 'warning',
          })
        }
      }

      this.open = false
      this.showCrispButton()
      this.$root.$emit('drmIntegrationFlowClose')
    },
    hideCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton && this.isMobileBreakpoint) {
        crispButton.style.setProperty('display', 'none', 'important')
      }
    },
    showCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton) {
        crispButton.style.removeProperty('display')
      }
    },
    checkStepCompletion(newVal, oldVal) {
      if (newVal <= oldVal) return

      const stepsToCheck = []
      for (let i = oldVal + 1; i <= newVal; i++) {
        if (i > this.panelIndexMap.COPY && !this.isCopyCheckpoint) break
        stepsToCheck.push(i)
      }

      stepsToCheck.forEach((step) => {
        if (!this.completedPanels.includes(step)) {
          this.completedPanels.push(step)
        }
      })
    },
    async createOrUpdateDrmGroup() {
      this.isFetching = true

      const payload = {
        name: this.groupName,
        percent_ts: 0.1,
        'font-family': 'rubik',
        'font-color': '#FFFFFF',
        'font-size': '12',
        'box-color': '#00000066',
        'box-opacity': '1',
        video_ids: [],
        active: false,
      }

      try {
        let response
        const isEditing = Boolean(this.drmGroup?.id)

        if (isEditing) {
          response = await drmApi.drmUpdateVideos(this.drmGroup.id, {
            name: this.groupName,
          })
        } else {
          response = await drmApi.createDrmGroup(payload)
        }

        this.drmGroup = response.data
        this.drmGroup.webhook_source_name = 'cademi'

        if (!isEditing) {
          await this.updateWebhook(this.drmGroup.id)
        }
      } catch (error) {
        console.error('Error creating/updating group:', error)
      } finally {
        this.isFetching = false
      }
    },
    async activateDrmGroup() {
      try {
        await drmApi.drmUpdateVideos(this.drmGroup.id, { active: true })
        this.drmGroup.active = true
      } catch (error) {
        console.error('Error activating group:', error)
      }
    },
    async updateWebhook(drmGroupId) {
      const webhookObj = {
        source_name: 'cademi',
        model_string1: '',
        model_string2: '',
        model_string3: '',
        token_verification: '',
      }

      await drmApi.updateGroupWebhook(webhookObj, drmGroupId)
    },
  },
}
</script>

<style scoped lang="scss">
::v-deep .v-dialog {
  overflow: hidden !important;
}

::v-deep .v-toolbar {
  height: 100% !important;
  min-height: 80px !important;

  .v-toolbar__content {
    height: 100% !important;
    min-height: 80px !important;
  }
}

::v-deep {
  .v-label {
    left: -10px !important;
  }

  input::placeholder {
    font-size: 14px !important;
  }
}

::v-deep .v-text-field__slot textarea::placeholder {
  font-size: 14px !important;
}

.card-actions {
  border-top: 1px solid var(--v-neutral-95-lightest-base) !important;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
}

.card-info {
  border-radius: 8px;
  border: 1px solid varFigma(neutral-95-lightest);
  background: varFigma(neutral-100-white);
  padding: 16px;
  margin-bottom: 16px;
}

::v-deep .panel-5-content {
  .base-input.v-input.v-input--is-readonly.filled .v-text-field__slot input {
    color: varFigma(neutral-60) !important;
  }

  .base-input.v-input.filled .v-text-field__slot .v-label {
    color: varFigma(neutral-40-light) !important;
  }

  .base-input .v-input__control {
    padding: 0 12px;
    border-radius: 4px;
    border: 1px solid varFigma(neutral-95-lightest);
    background: varFigma(neutral-100-white);
  }

  .base-textarea.v-input.v-input--is-readonly .v-label {
    top: -18px !important;
    left: -10px !important;
  }
}

::v-deep .v-input__slot {
  min-height: 32px !important;
  height: initial !important;
  background: varFigma(neutral-100-white) !important;

  textarea {
    margin-top: 0 !important;
    font-size: 14px !important;
    padding-top: 6px !important;
    line-height: 150% !important;
    pointer-events: none !important;
  }
}

::v-deep .input-cademi .v-input__slot {
  .v-input__append-inner {
    margin: auto !important;
  }
}

::v-deep .step-header-1 .v-expansion-panel-header__icon {
  cursor: not-allowed !important;
}

a:hover {
  text-decoration: underline !important;
}

@media (max-width: 480px) {
  .panel-5-content {
    flex-direction: column;
    gap: 16px;

    .v-input,
    .base-button {
      width: 100%;
    }
  }

  .copy-icon {
    display: none;
  }
}

@media (min-width: 768px) {
  .v-card__text {
    max-height: 648px;
    overflow: auto;
  }
}
</style>
